<div
  class="ui-line-chart"
  role="figure"
  [attr.aria-label]="title() || 'Gráfico de línea'"
>
  @if (title() && variant() !== 'sparkline') {
    <h3 class="ui-line-chart__title">{{ title() }}</h3>
  }

  <div
    class="ui-line-chart__canvas-wrap"
    (mousemove)="onMouseMove($event)"
    (mouseleave)="onCanvasLeave()"
  >
    @if (loading()) {
      <div
        class="ui-line-chart__skeleton"
        [style.width.px]="width()"
        [style.height.px]="height()"
        aria-hidden="true"
      ></div>
    } @else {
      <svg
        class="ui-line-chart__svg"
        [attr.width]="width()"
        [attr.height]="height()"
        [attr.viewBox]="'0 0 ' + width() + ' ' + height()"
        aria-hidden="true"
        overflow="visible"
      >
        <!-- ── Gradientes para áreas ──────────────────────────────── -->
        <defs>
          @for (lp of linePaths(); track lp.seriesIdx) {
            <linearGradient
              [id]="'ui-line-grad-' + lp.seriesIdx"
              x1="0" y1="0" x2="0" y2="1"
            >
              <stop
                offset="0%"
                [attr.stop-color]="lp.fillColor"
                [attr.stop-opacity]="gradStopOpacity(lp)"
              />
              <stop
                offset="100%"
                [attr.stop-color]="lp.fillColor"
                stop-opacity="0"
              />
            </linearGradient>
          }
        </defs>

        <!-- ── Grupo principal desplazado por los márgenes ──────────── -->
        <g [attr.transform]="'translate(' + margin().left + ',' + margin().top + ')'">

          <!-- ── Gridlines Y + Labels Y ────────────────────────────── -->
          @if (variant() !== 'sparkline') {
            @for (tick of yTicks(); track tick.value) {
              @if (showGrid()) {
                <line
                  class="ui-line-chart__grid"
                  x1="0" [attr.y1]="tick.y"
                  [attr.x2]="plotW()" [attr.y2]="tick.y"
                />
              }
              <text
                class="ui-line-chart__axis-label ui-line-chart__axis-label--y"
                [attr.x]="-8"
                [attr.y]="tick.y"
                text-anchor="end"
                dominant-baseline="middle"
              >{{ tick.label }}</text>
            }

            <!-- Eje Y -->
            <line
              class="ui-line-chart__axis"
              x1="0" y1="0"
              x2="0" [attr.y2]="plotH()"
            />

            <!-- Línea base (eje X) -->
            <line
              class="ui-line-chart__axis"
              [attr.x1]="0"  [attr.y1]="plotH()"
              [attr.x2]="plotW()" [attr.y2]="plotH()"
            />

            <!-- Línea del cero (cuando hay negativos) -->
            @if (zeroY() < plotH() - 2) {
              <line
                class="ui-line-chart__zero-line"
                [attr.x1]="0"       [attr.y1]="zeroY()"
                [attr.x2]="plotW()" [attr.y2]="zeroY()"
              />
            }

            <!-- Labels eje X -->
            @for (tick of xTicks(); track tick.label) {
              <text
                class="ui-line-chart__axis-label ui-line-chart__axis-label--x"
                [attr.x]="tick.x"
                [attr.y]="plotH() + 16"
                text-anchor="middle"
                dominant-baseline="hanging"
              >{{ tick.label }}</text>
            }

            <!-- Etiqueta eje Y -->
            @if (yLabel()) {
              <text
                class="ui-line-chart__axis-title"
                [attr.transform]="'rotate(-90) translate(' + (-(plotH() / 2)) + ',' + (-44) + ')'"
                text-anchor="middle"
              >{{ yLabel() }}</text>
            }

            <!-- Etiqueta eje X -->
            @if (xLabel()) {
              <text
                class="ui-line-chart__axis-title"
                [attr.x]="plotW() / 2"
                [attr.y]="plotH() + 38"
                text-anchor="middle"
              >{{ xLabel() }}</text>
            }
          }

          <!-- ── Área fill (detrás de las líneas) ──────────────────── -->
          @for (lp of linePaths(); track lp.seriesIdx) {
            @if (lp.areaPath) {
              <path
                class="ui-line-chart__area"
                [class.ui-line-chart__area--animated]="animated()"
                [attr.d]="lp.areaPath"
                [attr.fill]="areaFill(lp)"
                [attr.fill-opacity]="areaFillOpacity(lp)"
                stroke="none"
                [style.--line-delay]="lp.seriesIdx * 0.08 + 's'"
              />
            }
          }

          <!-- ── Líneas ──────────────────────────────────────────────── -->
          @for (lp of linePaths(); track lp.seriesIdx) {
            @if (lp.linePath) {
              <path
                class="ui-line-chart__line"
                [class.ui-line-chart__line--animated]="animated()"
                [attr.d]="lp.linePath"
                [attr.stroke]="lp.strokeColor"
                [attr.stroke-width]="lineWidth()"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                pathLength="1"
                style="pointer-events: none;"
                [style.--line-delay]="lp.seriesIdx * 0.08 + 's'"
              />
            }
          }

          <!-- ── Puntos ─────────────────────────────────────────────── -->
          @for (pt of allPoints(); track pt.seriesIdx + '-' + pt.catIdx) {
            @if (showDot(pt)) {
              <g
                class="ui-line-chart__dot-group"
                [class.ui-line-chart__dot-group--draw-in]="animated() && showDots() === 'always'"
                [class.ui-line-chart__dot-group--sparkline-last]="variant() === 'sparkline' && pt.catIdx === categories().length - 1"
                [style.--dot-delay]="animated() && showDots() === 'always' ? dotDelay(pt) : '0s'"
                style="pointer-events: none;"
              >
                <!-- Halo para punto activo -->
                @if (isActive(pt)) {
                  <circle
                    [attr.cx]="pt.x"
                    [attr.cy]="pt.y"
                    [attr.r]="dotRadius() + 6"
                    [attr.fill]="seriesColor(pt.seriesIdx)"
                    fill-opacity="0.18"
                    stroke="none"
                  />
                }
                <!-- Halo de hover -->
                @if (isHoveredCat(pt.catIdx) && !isActive(pt)) {
                  <circle
                    [attr.cx]="pt.x"
                    [attr.cy]="pt.y"
                    [attr.r]="dotRadius() + 4"
                    [attr.fill]="seriesColor(pt.seriesIdx)"
                    fill-opacity="0.12"
                    stroke="none"
                  />
                }
                <!-- Punto principal -->
                <circle
                  class="ui-line-chart__dot"
                  [class.ui-line-chart__dot--hovered]="isHoveredCat(pt.catIdx)"
                  [class.ui-line-chart__dot--active]="isActive(pt)"
                  [attr.cx]="pt.x"
                  [attr.cy]="pt.y"
                  [attr.r]="dotR(pt)"
                  [attr.fill]="isActive(pt) || isHoveredCat(pt.catIdx) ? seriesColor(pt.seriesIdx) : '#fff'"
                  [attr.stroke]="seriesColor(pt.seriesIdx)"
                  [attr.stroke-width]="isActive(pt) ? 2.5 : 2"
                />
                <!-- Etiqueta de valor -->
                @if (showLabel(pt)) {
                  <text
                    class="ui-line-chart__point-label"
                    [attr.x]="pt.x"
                    [attr.y]="pt.y - dotR(pt) - 6"
                    text-anchor="middle"
                    dominant-baseline="auto"
                    style="pointer-events: none;"
                  >{{ pt.rawValue }}</text>
                }
              </g>
            }
          }

          <!-- ── Crosshair + indicadores verticales ──────────────────── -->
          @if (crosshairX() !== null && variant() !== 'sparkline') {
            <line
              class="ui-line-chart__crosshair"
              [attr.x1]="crosshairX()"
              y1="0"
              [attr.x2]="crosshairX()"
              [attr.y2]="plotH()"
            />
            <!-- Tick vertical en eje X -->
            <line
              class="ui-line-chart__crosshair-tick"
              [attr.x1]="crosshairX()"
              [attr.y1]="plotH()"
              [attr.x2]="crosshairX()"
              [attr.y2]="plotH() + 5"
            />
          }

          <!-- ── Hit areas (columna invisible por categoría) ──────── -->
          @for (cat of categories(); track cat.id; let ci = $index) {
            <rect
              class="ui-line-chart__hit-col"
              [attr.x]="colHitBounds(ci).x"
              y="0"
              [attr.width]="colHitBounds(ci).width"
              [attr.height]="plotH()"
              fill="transparent"
              stroke="none"
              (mouseenter)="onCatEnter(ci)"
              (click)="hoveredPoints().length && onPointClick(hoveredPoints()[0])"
              [attr.role]="'img'"
              [attr.tabindex]="0"
              [attr.aria-label]="cat.label"
            />
          }

        </g>
      </svg>
    }

    <!-- ── Tooltip flotante multi-serie ──────────────────────────────── -->
    @if (tooltipVisible() && tooltipData()) {
      <div
        class="ui-line-chart__tooltip"
        [style.left.px]="tooltipX()"
        [style.top.px]="tooltipY()"
        aria-hidden="true"
      >
        <span class="ui-line-chart__tooltip-cat">{{ tooltipData()!.label }}</span>
        @for (entry of tooltipData()!.entries; track entry.seriesLabel) {
          <div class="ui-line-chart__tooltip-row">
            <span
              class="ui-line-chart__tooltip-dot"
              [style.background]="entry.color"
            ></span>
            <span class="ui-line-chart__tooltip-series">{{ entry.seriesLabel }}</span>
            <span class="ui-line-chart__tooltip-value">{{ entry.value }}</span>
          </div>
        }
      </div>
    }
  </div>

  <!-- ── Leyenda de series ────────────────────────────────────────────── -->
  @if (showLegend() && !loading() && series().length > 1 && variant() !== 'sparkline') {
    <ul class="ui-line-chart__legend" role="list" aria-label="Leyenda del gráfico">
      @for (sr of series(); track sr.id; let i = $index) {
        <li class="ui-line-chart__legend-item" role="listitem">
          <svg width="20" height="10" aria-hidden="true">
            <line
              x1="0" y1="5" x2="14" y2="5"
              [attr.stroke]="seriesColor(i)"
              stroke-width="2.5"
              stroke-linecap="round"
            />
            <circle
              cx="7" cy="5" r="3"
              fill="white"
              [attr.stroke]="seriesColor(i)"
              stroke-width="2"
            />
          </svg>
          <span class="ui-line-chart__legend-label">{{ sr.label }}</span>
        </li>
      }
    </ul>
  }
</div>
